/// <amd-module name="VC/Markdown/Plugins/PathUtilities/VersionControlUrlUtilities" />

/*
 * This file was ported from the legacy webplatform
 * Logic that is not needed for markdown token transformation was not ported over.
 * Logic has only been changed where necessary to interact in the modern web platform.
 * There is no intention to change any factorings or style otherwise.
 */
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from 'VSS/Platform/Location';
import * as Git from "VC/Common/Contracts/Git";

const VC_IMAGE_TRANSFORMER_ROUTES_CONTRIBUTION: string = "ms.vss-code-web.markdown-image-transformer-route";
const VC_TFVC_LINK_TRANSFORMER_ROUTE_CONTRIBUTION: string = "ms.vss-code-web.tfvc-files-route";
const VC_GIT_LINK_TRANSFORMER_ROUTE_CONTRIBUTION: string = "ms.vss-code-web.files-route-git";
const GIT_ITEMS_API_VERSION = "5.0-preview.1";
const TFVC_ITEMS_API_VERSION = "4.1-preview.1";

export enum RepositoryType {
    Tfvc = 0,
    Git = 1
}

/**
 * Get the URL for displaying/downloading file content using the Items REST API.
 * Automatically resolves Git LFS pointers to content from LFS.
 * @param contentOnly if true and has a safe content-type, content will be displayed in the browser, else it is downloaded
 * @param routeData override route values - primarily used for the project for TFVC paths outside the current project
 * @param itemVersion optional information about Git branch to query
 */
export function getFileContentUrl(
    context: IVssPageContext,
    repositoryId: string,
    repositoryType: RepositoryType,
    projectId: string,
    path: string,
    contentOnly: boolean = false,
    routeData: any = null,
    itemVersion?: Git.GitVersionDescriptor
    ): string {

    const download = !contentOnly;
    let fileContent: string;
    if (repositoryType == RepositoryType.Git) {
        fileContent = getGitFileContentUrl(context, projectId, repositoryId, path, download, true, routeData, itemVersion);
    }
    else {
        fileContent = getTfvcFileContentUrl(context, projectId, path, download, routeData);
    }
    return fileContent;
}

export function getExplorerUrl(context: IVssPageContext, repositoryType: RepositoryType, path?: string, action?: string, state?: any, routeData?: any): string {
    routeData = {
        ...routeData || {},
        ...state || {},
    };

    if (action) {
        routeData._a = action;
    }

    if (path) {
        routeData.path = path;
    }

    const locationService = context.getService<IVssLocationService>("IVssLocationService");
    return repositoryType === RepositoryType.Git
        ? locationService.routeUrl(VC_GIT_LINK_TRANSFORMER_ROUTE_CONTRIBUTION, { ...routeData })
        : locationService.routeUrl(VC_TFVC_LINK_TRANSFORMER_ROUTE_CONTRIBUTION, { ...routeData });
}

function getGitFileContentUrl(
    context: IVssPageContext,
    project: string,
    repositoryId: string,
    path: string,
    download?: boolean,
    resolveLfs?: boolean,
    routeData?: any,
    itemVersion?: Git.GitVersionDescriptor
    ): string {

    const area = "git";
    const resource = "Items";
    const routeValues = {
        project,
        repositoryId,
        ...routeData
    };

    const queryValues: any = {
        path,
        download,
        resolveLfs,
        '$format': "octetStream",
        'api-version': GIT_ITEMS_API_VERSION
    };

    if (itemVersion){
        queryValues["versionDescriptor[versionOptions]"] = itemVersion.versionOptions || 0;
        queryValues["versionDescriptor[versionType]"] = itemVersion.versionType || 0;
        queryValues["versionDescriptor[version]"] = itemVersion.version;
    }

    return getRequestUrl(context, area, resource, routeValues, queryValues);
}

function getTfvcFileContentUrl(
    context: IVssPageContext,
    project: string,
    path: string,
    download?: boolean,
    routeData?: any): string {

    const area = "tfvc";
    const resource = "Items";
    const routeValues = {
        project,
        ...routeData
    };
    const queryValues: any = {
        path,
        download,
        '$format': "octetStream",
        'api-version': TFVC_ITEMS_API_VERSION
    };

    return getRequestUrl(context, area, resource, routeValues, queryValues);
}


function getRequestUrl(context: IVssPageContext, area: string, resource: string, routeValues: any, queryParams?: {[key: string]: any}): string {
    // Add area/resource route values (based on the location)
    routeValues = routeValues || {};
    if (!routeValues.area) {
        routeValues.area = area;
    }

    if (!routeValues.resource) {
        routeValues.resource = resource;
    }

    const locationService = context.getService<IVssLocationService>("IVssLocationService");
    let url = locationService.routeUrl(VC_IMAGE_TRANSFORMER_ROUTES_CONTRIBUTION, { ...routeValues, ...queryParams });

    return url;
}